import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Per-board column visibility for the kanban boards.
 *
 * The product ask is an on/off switch per column, so a board can be collapsed
 * to the stages one person actually works. It has to be remembered per board —
 * deals, quotes, invoices and, shortly, deliverables — because the stages have
 * nothing to do with each other, and hiding "Closed Lost" on deals says nothing
 * about which invoice statuses matter.
 *
 * Two decisions carry the whole file, and both are about failing safe:
 *
 *  1. **The HIDDEN ids are stored, never the visible ones.** Storing the
 *     visible set makes every column that appears later — a new row in the
 *     stage master — default to hidden, so it silently disappears from the
 *     board of everyone who ever touched the control, with no way to discover
 *     it short of clearing storage. That exact bug reached this repo once
 *     already through a persisted sidebar accordion and made the catalog pages
 *     look broken. Storing the hidden set inverts it: anything the board gains
 *     is visible, and a stored preference can only ever subtract.
 *  2. **A board is never left with nothing on it.** See {@link toggle}.
 */

/**
 * One key holding `{ [boardName]: (number|null)[] }` rather than a key per
 * board.
 *
 * A key per board reads tidier but leaves `emerge.kanban.hiddenColumns.<name>`
 * entries scattered through storage that nothing can ever sweep — rename a
 * board or drop one and its key outlives the app, unfindable without already
 * knowing the old name. One map is a single thing to read, a single thing to
 * clear, and it is tiny: a handful of integers per board. It also matches the
 * one-key-per-preference shape in `use-sidebar.ts`.
 */
const HIDDEN_KEY = 'emerge.kanban.hiddenColumns';

/** `{ [boardName]: hidden column ids }`. `null` is the "Unassigned" column. */
type StoredMap = Record<string, (number | null)[]>;

/**
 * `null` is a REAL column id here — the "Unassigned" column — and must be
 * hideable like any other, so it can never be treated as "no value". Two
 * consequences the rest of the file depends on:
 *
 *  - the ids live in a JSON **array**, where `null` round-trips as itself. A
 *    map keyed by id would stringify it to the key `"null"`, which is also what
 *    a column genuinely named that would produce;
 *  - every filter goes through this guard rather than a truthiness test, which
 *    would silently drop `null` (and `0`, if a master ever seeds one).
 */
function isColumnId(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isInteger(value));
}

/**
 * The stored value is a JSON object of board name to hidden ids.
 *
 * Anything else — a hand-edited value, a half-written one, an array left by
 * some future shape — parses to "nothing hidden" rather than throwing. The cost
 * of a malformed value is then a board showing every column, which is a state
 * the user can see and fix; a crash over a display preference never is.
 */
function parseStoredMap(raw: string | null): StoredMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const map: StoredMap = {};
    for (const [board, ids] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(ids)) map[board] = ids.filter(isColumnId);
    }
    return map;
  } catch {
    return {};
  }
}

/** Storage throws outright in some private-browsing modes, so every touch is guarded. */
function readStoredMap(): StoredMap {
  try {
    return parseStoredMap(localStorage.getItem(HIDDEN_KEY));
  } catch {
    return {};
  }
}

/**
 * Keep only the ids the board still has, the way `use-sidebar` keeps only the
 * nav labels that still exist.
 *
 * Without it the stored set grows forever, and worse: column ids come from a
 * master table, so a stale id can be recycled onto a *different* column and
 * hide something the user never chose to hide.
 *
 * **An empty `allowed` means the board has not loaded yet, not that it has no
 * columns.** Pruning against it would wipe the preference in the gap between
 * mount and the first fetch resolving, so nothing is dropped until there is a
 * real board to compare against.
 */
function pruneToBoard(
  ids: Set<number | null>,
  allowed: ReadonlySet<number | null>,
): Set<number | null> {
  if (allowed.size === 0) return ids;
  const kept = new Set<number | null>();
  for (const id of ids) if (allowed.has(id)) kept.add(id);
  // Same membership: hand back the identical Set so the caller's setState is a no-op.
  return kept.size === ids.size ? ids : kept;
}

function readHidden(boardName: string, allowed: ReadonlySet<number | null>): Set<number | null> {
  return pruneToBoard(new Set(readStoredMap()[boardName] ?? []), allowed);
}

/**
 * Write one board's hidden set, leaving every other board's alone — the map is
 * re-read first because another tab, or another board mounted in this one, may
 * have written since.
 *
 * An empty set removes the entry rather than storing `[]`, so a user who shows
 * everything again leaves nothing behind.
 */
function writeHidden(boardName: string, ids: ReadonlySet<number | null>): void {
  try {
    const map = readStoredMap();
    if (ids.size === 0) delete map[boardName];
    else map[boardName] = [...ids];
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(map));
  } catch {
    // Persisting is a convenience; the session still works without it.
  }
}

export interface ColumnVisibility {
  /** True when the column should render. */
  isVisible: (columnId: number | null) => boolean;
  /** Flip one column. */
  toggle: (columnId: number | null) => void;
  /** Show every column again. */
  showAll: () => void;
  /** How many are currently hidden — for a "3 hidden" badge. */
  hiddenCount: number;
  /** True when at least one is hidden, so the control can signal it. */
  hasHidden: boolean;
}

/**
 * What this session has chosen, tagged with the board it was chosen on.
 *
 * The board name is carried alongside the ids rather than read from the
 * argument, because a hook whose `boardName` changes would otherwise apply one
 * board's choices to another for the render before any resync — and persist
 * them there. Tagging makes a mismatch detectable, and a mismatch simply falls
 * back to what storage holds for the new board.
 */
interface BoardHidden {
  board: string;
  ids: Set<number | null>;
}

export function useColumnVisibility(
  boardName: string,
  /** Every column id currently on the board, including null for "Unassigned". */
  columnIds: (number | null)[],
): ColumnVisibility {
  /**
   * **`columnIds` is a new array on every render** — it is derived from the
   * fetched board in the caller's render body. Depending on it directly is the
   * same trap as the kanban params object in `use-kanban.ts`: every memo below
   * would recompute every render, and the persist effect would rewrite storage
   * every render — and any effect that also set state would loop outright. A
   * string compares by value, so everything below depends on THIS.
   *
   * Ids are filtered on the way in so a stray `undefined` cannot be serialised
   * to `null` and collide with the Unassigned column, and the result stays
   * parseable back into the id list — the same trick `paramsKeyOf` uses.
   */
  const columnsKey = JSON.stringify(columnIds.filter(isColumnId));

  const allowedIds = useMemo<ReadonlySet<number | null>>(
    () => new Set(JSON.parse(columnsKey) as (number | null)[]),
    [columnsKey],
  );

  /** Only what this session has toggled; `null` until the user touches a switch. */
  const [chosen, setChosen] = useState<BoardHidden | null>(null);

  /**
   * The hidden set is DERIVED, not synchronised.
   *
   * Storage is read here rather than in an effect for the same reason
   * `use-sidebar` reads at initialisation: applying the preference after first
   * paint would show the whole board and then collapse it on every load. It is
   * read in a memo rather than only in a `useState` initialiser because
   * `boardName` and the column set both change over the hook's life, and
   * re-deriving is strictly simpler than an effect that writes state back —
   * that shape cascades a render, and it is where the board-changed/ids-stale
   * mismatch would live.
   *
   * A read is pure, so repeating it under StrictMode costs nothing. The prune
   * is applied to both paths, so an id the board has lost cannot leak through
   * whichever one produced the set.
   */
  const hidden = useMemo(() => {
    if (chosen && chosen.board === boardName) return pruneToBoard(chosen.ids, allowedIds);
    return readHidden(boardName, allowedIds);
  }, [chosen, boardName, allowedIds]);

  /*
   * Writing on mount as well as after a toggle is deliberate: it is how an id
   * the board no longer has actually leaves storage, rather than sitting there
   * until the user happens to toggle something. Re-writing what was just read
   * is otherwise a no-op.
   */
  useEffect(() => {
    writeHidden(boardName, hidden);
  }, [boardName, hidden]);

  /**
   * Counted as the intersection with the board rather than as `hidden.size`.
   *
   * The two agree once the board has loaded, but not before: pruning is
   * deliberately skipped while `columnIds` is empty, so a returning user's
   * stored ids are all still in the set. Counting them would flash "3 hidden"
   * over a board that is not showing anything yet.
   */
  const hiddenCount = useMemo(() => {
    let count = 0;
    for (const id of allowedIds) if (hidden.has(id)) count += 1;
    return count;
  }, [allowedIds, hidden]);

  /** A Set lookup: this runs once per column per render, so it must be O(1). */
  const isVisible = useCallback((columnId: number | null) => !hidden.has(columnId), [hidden]);

  /**
   * **Hiding the last visible column is refused, not obeyed.**
   *
   * The alternative — quietly calling `showAll` instead — was rejected: it
   * answers a click on one column by changing several others, which reads as a
   * bug even when it is kind. Refusing leaves the board exactly as it was, and
   * the caller can disable that one switch (it is the last one whose
   * `isVisible` is true) so the refusal is visible before the click rather than
   * felt after it. Either way an empty board never renders, because a board
   * with no columns and no explanation is indistinguishable from a broken one.
   */
  const toggle = useCallback(
    (columnId: number | null) => {
      // A new Set every time — mutating the held one would not re-render.
      const next = new Set(hidden);
      if (!next.delete(columnId)) {
        next.add(columnId);
        let visibleAfter = 0;
        for (const id of allowedIds) if (!next.has(id)) visibleAfter += 1;
        if (visibleAfter === 0) return;
      }
      setChosen({ board: boardName, ids: next });
    },
    [hidden, allowedIds, boardName],
  );

  const showAll = useCallback(() => {
    if (hidden.size === 0) return;
    setChosen({ board: boardName, ids: new Set() });
  }, [hidden, boardName]);

  return { isVisible, toggle, showAll, hiddenCount, hasHidden: hiddenCount > 0 };
}
